import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ButtonSlim from '../../../../web-app-components/components/ButtonSlim';
import TextInput from '../../../../web-app-components/components/TextInput';
import useStateThrottled from '../../../../web-app-components/hooks/useStateThrottled';
import makeStyles from '@material-ui/core/styles/makeStyles';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import SearchIcon from '@material-ui/icons/Search';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import { DataGrid } from '@material-ui/data-grid';

import discountCodesClient from '../../../../clients/discount-codes';

const targetLabels = {
  routes: 'Maršrutai',
};

const columns = [
  {
    field: 'Code',
    headerName: 'Kodas',
    flex: 1,
    sortable: false,
  },
  {
    field: 'DiscountPercent',
    headerName: 'Nuolaida (%)',
    flex: 0.5,
    sortable: false,
    valueGetter: (params) => `${params.row?.DiscountPercent ?? '—'}%`,
  },
  {
    field: 'Target',
    headerName: 'Paskirtis',
    flex: 0.5,
    sortable: false,
    valueGetter: (params) => targetLabels[params.row?.Target] ?? params.row?.Target ?? '—',
  },
  {
    field: 'UsageCount',
    headerName: 'Panaudota',
    flex: 0.4,
    sortable: false,
    valueGetter: (params) => `${params.row?.UsageCount ?? 0} / ${params.row?.UsageLimit ?? 0}`,
  },
  {
    field: 'Active',
    headerName: 'Aktyvus',
    flex: 0.4,
    sortable: false,
    valueGetter: (params) => (params.row?.Active !== false ? 'Taip' : 'Ne'),
  },
];

const useStyles = makeStyles((theme) => ({
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    '& > :not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
  searchInput: {
    flex: '0 1 300px',
  },
}));

const DiscountsList = () => {
  const classes = useStyles();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { enqueueSnackbar } = useSnackbar();

  const [throttledSearchTerm, searchTerm, setSearchTerm] = useStateThrottled('', 500);
  const [discounts, setDiscounts] = useState();

  useEffect(() => {
    (async () => {
      try {
        const query = { sort: 'createdAt:desc' };
        const filtersObj = {};
        if (throttledSearchTerm && throttledSearchTerm.trim()) {
          filtersObj.Code = { $containsi: throttledSearchTerm.trim() };
        }
        if (Object.keys(filtersObj).length > 0) {
          query.filters = filtersObj;
        }
        const result = await discountCodesClient.find(query);
        const list = Array.isArray(result) ? result : result?.data ?? [];
        setDiscounts(
          list.map((row) => ({
            ...row,
            id: row.documentId ?? row.id ?? row.Code,
          }))
        );
      } catch (error) {
        enqueueSnackbar('Nepavyko gauti nuolaidų kodų sąrašo. Bandykite atidaryti iš naujo.', {
          variant: 'error',
        });
      }
    })();
  }, [throttledSearchTerm, enqueueSnackbar]);

  const handleSearchChange = ({ target: { value } }) => setSearchTerm(value);
  const handleNewClick = () => history.push(`${path}/new`);
  const handleRowClick = ({ id }) => history.push(`${path}/${id}`);

  return (
    <div>
      <div className={classes.controls}>
        <TextInput
          className={classes.searchInput}
          placeholder="Ieškoti pagal kodą..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <ButtonSlim startIcon={<AddIcon />} onClick={handleNewClick}>
          Naujas nuolaidos kodas
        </ButtonSlim>
      </div>
      <Paper>
        <DataGrid
          columns={columns}
          rows={discounts || []}
          loading={discounts === undefined}
          onRowClick={handleRowClick}
          autoHeight
          disableColumnMenu
          disableSelectionOnClick
        />
      </Paper>
    </div>
  );
};

export default DiscountsList;
